import type { PropertyDetailsFragment } from '__generated__/athena/graphql';
import { Badge, ListingHistoryState, ListingState } from '__generated__/athena/graphql';

import { isBuyDirectEligible } from './buyDirectHelpers';

/**
 * Returns the sold price (in dollars) for a recently-sold home by reading the
 * SOLD entry in `listingHistory`. Returns null when there is no SOLD entry or
 * no sold price was recorded (e.g. an off-market sale without a price).
 */
export const getSoldPrice = (property: PropertyDetailsFragment): number | null => {
  const soldEntry = property.listingHistory?.find(
    (entry) => entry.state === ListingHistoryState.SOLD,
  );
  const soldPriceCents = soldEntry?.soldPriceCents;
  if (!soldPriceCents) return null;
  return soldPriceCents / 100;
};

const isRecentlySold = (property: PropertyDetailsFragment): boolean =>
  property.listingState === ListingState.RECENTLY_SOLD ||
  property.displayBadge === Badge.RECENTLY_SOLD;

/**
 * Returns the price (in dollars) to surface on a property's primary price
 * displays (photo carousel, summary, lightbox). Recently-sold homes show their
 * sold price, falling back to the list price when no sold price was recorded;
 * active BuyDirect-eligible homes show the BuyDirect price, falling back to the
 * list price when no BuyDirect price was recorded; everything else shows the
 * list price. Returns null when no price is available.
 */
export const getDisplayPrice = (property: PropertyDetailsFragment): number | null => {
  if (isRecentlySold(property)) {
    return getSoldPrice(property) ?? property.listPrice ?? null;
  }
  if (property.displayBadge === Badge.ON_THE_MARKET && isBuyDirectEligible(property)) {
    return property.buyDirectPrice ?? property.listPrice ?? null;
  }
  return property.listPrice ?? null;
};
